import { css } from "@emotion/react";
import { fontSizeLineHeight, fonts, mediaQueries, pxToRem, rgbaFromHex, weights, transition } from "./css-utils";
import { colors } from "./colors";

export const h1 = css`
  ${fontSizeLineHeight(30, 40)}
  margin-bottom: ${pxToRem(24)};
  font-family: ${fonts.display};
  font-weight: ${weights.black};
  color: ${colors.white};

  ${mediaQueries.md} {
    ${fontSizeLineHeight(36, 48)}
  }

  ${mediaQueries.lg} {
    ${fontSizeLineHeight(60, 84)}
    margin-bottom: ${pxToRem(32)};
  }
`;

export const h2 = css`
  ${fontSizeLineHeight(24, 36)}
  margin-bottom: ${pxToRem(16)};
  font-family: ${fonts.display};
  font-weight: ${weights.black};
  color: ${colors.white};
  
  ${mediaQueries.md} {
    ${fontSizeLineHeight(30, 40)}
  }

  ${mediaQueries.lg} {
    ${fontSizeLineHeight(48, 64)}
    margin-bottom: ${pxToRem(24)};
  }
`;

export const h3 = css`
  ${fontSizeLineHeight(20, 32)}
  margin-bottom: ${pxToRem(16)};
  font-family: ${fonts.display};
  font-weight: ${weights.black};
  color: ${colors.white};

  ${mediaQueries.md} {
    ${fontSizeLineHeight(24, 36)}
  }

  ${mediaQueries.lg} {
    ${fontSizeLineHeight(36, 48)}
    margin-bottom: ${pxToRem(24)};
  }
`;

export const h4 = css`
  ${fontSizeLineHeight(18, 28)}
  margin-bottom: ${pxToRem(8)};
  font-family: ${fonts.display};
  font-weight: ${weights.black};
  color: ${colors.white};
  
  ${mediaQueries.md} {
    ${fontSizeLineHeight(20, 32)}
  }

  ${mediaQueries.lg} {
    ${fontSizeLineHeight(24, 36)}
    margin-bottom: ${pxToRem(16)};
  }
`;

export const h5 = css`
  ${fontSizeLineHeight(16, 24)}
  margin-bottom: ${pxToRem(8)};
  font-family: ${fonts.display};
  font-weight: ${weights.black};
  color: ${colors.white};
  
  ${mediaQueries.md} {
    ${fontSizeLineHeight(18, 28)}
  }

  ${mediaQueries.lg} {
    ${fontSizeLineHeight(20, 32)}
    margin-bottom: ${pxToRem(16)};
  }
`;

export const h6 = css`
  ${fontSizeLineHeight(24, 36)}
  margin-bottom: ${pxToRem(16)};
  font-family: ${fonts.display};
  font-weight: ${weights.medium};
  color: ${colors.white};
`;

export const subtitle = css`
  ${fontSizeLineHeight(18,28)}
  font-family: "Space Grotesk", sans-serif;
  font-weight: ${weights.medium};
  color: ${colors.black200};
  ${mediaQueries.md} {
    ${fontSizeLineHeight(20, 32)} 
  }

  ${mediaQueries.lg} {
    ${fontSizeLineHeight(24, 36)} 
  }
`;

export const table = css`
  border-collapse: collapse;

  tbody tr {
      border-bottom: ${pxToRem(1)} solid ${colors.ghostWhite};
  }

  td {
    padding: ${pxToRem(24)} ${pxToRem(16)};
    vertical-align: top;
  }

  th {
    background: ${colors.ghostWhite};
    text-align:initial;
    padding: 0 ${pxToRem(16)};
  }

  td:nth-of-type(1) {  
    font-weight: ${weights.bold};
  }
`;

export const focusStyles = css`
  outline: none;
  box-shadow: 0 0 ${pxToRem(2)} ${pxToRem(2)} ${colors.yellow600};
`;

export const linkStyles = css`
  position: relative;
  color: ${colors.yellow500};
  cursor: pointer;

  &:focus-visible {
    ${focusStyles};
  }

  &:hover,
  &:active,
  &:focus {
    background-position: 0%;
  }

  &[disabled] {
    color: ${colors.lightGrey};
    
    &:hover, 
    &:active, 
    &:focus {
      background-position: 100% 0;
    }
  }
`;

export const bodySm = css`
  ${fontSizeLineHeight(16, 28)}
  margin-bottom: ${pxToRem(24)};
`;

export const bodyLg = css`
  ${fontSizeLineHeight(18, 32)}
  margin-bottom: ${pxToRem(32)};
`;

export const visuallyHidden = css`
  position: absolute;
  clip: rect(0 0 0 0);
  clip-path: polygon(0 0, 0 0, 0 0);
  width: ${pxToRem(1)};
  height: ${pxToRem(1)};
  padding: 0;
  margin: ${pxToRem(-1)};
  border: 0;
  overflow: hidden;
  white-space: nowrap;
`;

export const spaceLg = css`
  margin-bottom: ${pxToRem(80)};

  ${mediaQueries.lg} {
    margin-bottom: ${pxToRem(160)};
  }
`;

export const spaceMd = css`
  margin-bottom: ${pxToRem(56)};

  ${mediaQueries.lg} {
    margin-bottom: ${pxToRem(112)};
  }
`;

export const spaceSm = css`
  margin-bottom: ${pxToRem(32)};

  ${mediaQueries.lg} {
    margin-bottom: ${pxToRem(64)};
  }
`;

const listBase = css`
  ${bodySm}
  list-style-type: none;
  padding: 0;
  margin: 0;
    
  ${mediaQueries.md} {
    ${bodyLg}
    margin: 0;
  }
  
  li {
    position: relative;
   
    p {
      margin: 0;
    }

    &::before {
      position: absolute;
      left: 0;
    }
  }
`;

export const bullets = [listBase, css`
  li {
    margin-bottom: ${pxToRem(16)};
    padding-left: ${pxToRem(24)};

    &::before {
      content: '';
      width: ${pxToRem(8)};
      height: ${pxToRem(8)};
      top: ${pxToRem(12)};
      background: ${colors.violet600};
    }
    
    ul {
      padding-top: ${pxToRem(16)};
    }
  }
`];

export const checkmarks = [listBase, css`
  li {
    margin-bottom: ${pxToRem(16)};
    padding-left: ${pxToRem(36)};
    
    &::before {
      content: '';
      width: ${pxToRem(24)};
      height: ${pxToRem(24)};
      top: ${pxToRem(4)};
      background-repeat: no-repeat;
      background-position: center;
      background-size: ${pxToRem(24)} ${pxToRem(20)};
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='20' viewBox='0 0 24 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='tick'%3E%3Cpath id='Icon' d='M9.99999 15.1C9.69999 15.1 9.49999 15 9.29999 14.8L5.09999 10.6C4.69999 10.2 4.69999 9.60002 5.09999 9.20002C5.49999 8.80002 6.09999 8.80002 6.49999 9.20002L9.99999 12.7L17.5 5.20002C17.9 4.80002 18.5 4.80002 18.9 5.20002C19.3 5.60002 19.3 6.20002 18.9 6.60002L10.7 14.8C10.5 15 10.3 15.1 9.99999 15.1Z' fill='%23A090FF'/%3E%3C/g%3E%3C/svg%3E%0A");
    }
  }
`];

export const plus = [listBase, css`
  li {
    margin-bottom: ${pxToRem(16)};
    padding-left: ${pxToRem(36)};
    
    &::before {
      content: '';
      width: ${pxToRem(24)};
      height: ${pxToRem(24)};
      top: ${pxToRem(0)};
      background-repeat: no-repeat;
      background-position: center;
      background-size: ${pxToRem(24)} ${pxToRem(20)};
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='plus'%3E%3Cpath id='Icon' d='M12 6C11.4477 6 11 6.44772 11 7V11H7C6.44772 11 6 11.4477 6 12C6 12.5523 6.44772 13 7 13H11V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V13H17C17.5523 13 18 12.5523 18 12C18 11.4477 17.5523 11 17 11H13V7C13 6.44772 12.5523 6 12 6Z' fill='%23A090FF'/%3E%3C/g%3E%3C/svg%3E");
    }
  }
`];

export const numbers = [checkmarks, css`
  counter-reset: li;
  li::before {
    ${fontSizeLineHeight(16, 22)}
    content: counter(li);
    counter-increment: li;
    margin-bottom: 0;
    background-image: none;
    margin-bottom: ${pxToRem(8)};
    font-family: ${fonts.display};
    font-weight: ${weights.bold};
    color: ${colors.white};
    text-align: center;
    border-radius: 50%;
    background: ${colors.violet600};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ul li::before { /* So nested uls don't inherit the numbers */
    content: '';
    width: ${pxToRem(8)};
    height: ${pxToRem(8)};
    top: ${pxToRem(12)};
    counter-increment: none;
  }
`];

export const dropShadows = {
  small: `0 ${pxToRem(1)} ${pxToRem(4)} 0 ${rgbaFromHex(colors.grey, 10)}`,
  medium: `0 ${pxToRem(6)} ${pxToRem(24)} 0 ${rgbaFromHex(colors.grey, 10)}`,
  large: `0 ${pxToRem(8)} ${pxToRem(35)} 0 ${rgbaFromHex(colors.grey, 15)}`,
  xlarge: `0 ${pxToRem(8)} ${pxToRem(70)} 0 ${rgbaFromHex(colors.grey, 24)}`
};

export const center = css`
  text-align: center;
`;

export const gradientText = css`
  display: inline-block;
  background: rgb(250,65,255);
  background: linear-gradient(90deg, rgba(96,70,255,1) 0%, rgba(250,65,255,1) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const rainbowBorder = css`
  border: double ${pxToRem(1)} transparent;
  border-radius: ${pxToRem(32)};
  background-image: linear-gradient(#181e21, #181e21),
    linear-gradient(
      to bottom right,
      #b8fd33 0%,
      #fec837 25%,
      #fd1892 50%,
      #b827fc 75%,
      #2c90fc 100%
    );
  background-origin: border-box;
  background-clip: padding-box, border-box;
`;

export const rainbowBorderBlackBg = css`
  border: double ${pxToRem(1)} transparent;
  border-radius: ${pxToRem(32)};
  background-image: linear-gradient(#0C0F10, #0C0F10),
    linear-gradient(
      to bottom right,
      #b8fd33 0%,
      #fec837 25%,
      #fd1892 50%,
      #b827fc 75%,
      #2c90fc 100%
    );
  background-origin: border-box;
  background-clip: padding-box, border-box;
`;

export const rainbowBorderInverse = css`
  border: double ${pxToRem(1)} transparent;
  border-radius: ${pxToRem(32)};
  background-image: linear-gradient(#181e21, #181e21),
    linear-gradient(
      to bottom right,
      #b827fc 0%,
      #2c90fc 25%,
      #b8fd33 50%,
      #fec837 75%,
      #fd1892 100%
    );
  background-origin: border-box;
  background-clip: padding-box, border-box;
`;

export const globalStyles = css`
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=optional');
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=optional" rel="stylesheet');

  *, *::before, &::after {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    padding: 0;
    margin: 0;
    font-family: ${fonts.sans};
    color: ${colors.black300};
    line-height: 1.75;
    background-color: #0C0F10;
  }

  strong {
    font-weight: ${weights.bold};
  }
  
  h1, h2, h3, h4, h5, h6, p {
    scroll-margin: ${pxToRem(250)};
    margin-top: 0;
  }
  
  p {
    ${bodySm}
    
    ${mediaQueries.md} {
      ${bodyLg}
    }
  }

  h1:not([class]) {
    ${h1}
  }

  h2:not([class]) {
    ${h2}
  }
  
  h3:not([class]) {
    ${h3}
  }
  
  h4:not([class]) {
    ${h4}
  }
  
  h5:not([class]) {
    ${h5}
  }
  
  h6:not([class]) {
    ${h6}
  }

  table:not([class]) {
    ${table}
  }

  a:not([class]) {
    ${linkStyles}
  }
  
  ul:not([class]) {
    ${bullets}
  }
  
  ol:not([class]) {
    ${numbers}

    &[start="2"] {
      counter-reset: li 1;
    }

    &[start="3"] {
      counter-reset: li 2;
    }

    &[start="4"] {
      counter-reset: li 3;
    }

    &[start="5"] {
      counter-reset: li 4;
    }

    &[start="6"] {
      counter-reset: li 5;
    }

    &[start="7"] {
      counter-reset: li 6;
    }

    &[start="8"] {
      counter-reset: li 7;
    }
  }

  blockquote {
    margin: 0 0 ${pxToRem(40)};
    border-left: ${pxToRem(8)} solid ${colors.violet600};
    padding-left: ${pxToRem(32)};

    p:first-of-type {
      margin-bottom: ${pxToRem(40)};
      font-size: ${pxToRem(24)};
      line-height: 1.5;
      font-family: "Space Grotesk", sans-serif;
      font-weight: ${weights.medium};
      color: ${colors.white};
      grid-column-end: span 2;
    }

    p:last-of-type {
      margin-bottom: 0;
    }

    &:has(img) {
      display: grid;
      grid-template-columns: ${pxToRem(80)} auto;
      grid-template-rows: auto;
      column-gap: ${pxToRem(16)};
      align-items: center;
    }

    figure { /* Quotee headshot */
      margin: 0;
      line-height: 0;

      img { 
        border-radius: 50% !important;
      }
    }
  }

  code {
    max-width: 100%;
    white-space: nowrap;
    background-color: ${colors.neutral800};
    color: ${colors.snow};
    font-size: ${pxToRem(16)};
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(4)};
  }

  pre {
    overflow-x: auto;
    background-color: ${colors.neutral800};
    border-radius: ${pxToRem(4)};
    margin-bottom: ${pxToRem(32)};
    
    code {
      display: block;
      padding: ${pxToRem(4)} ${pxToRem(12)};
      white-space: inherit;
    }
  }
  
  /* code highlighting */
  /* @todo find similar colors from our brand system */
  .token.comment,
  .token.prolog,
  .token.doctype,
  .token.cdata {
    color: ${colors.neutral500};
  }

  .token.punctuation {
    color: ${colors.pink100};
  }

  .token.namespace {
    opacity: .7;
  }

  .token.property,
  .token.tag,
  .token.constant,
  .token.symbol,
  .token.deleted {
    color: ${colors.pink500};
  }

  .token.boolean,
  .token.number {
    color: ${colors.violet400};
  }

  .token.selector,
  .token.attr-name,
  .token.string,
  .token.char,
  .token.builtin,
  .token.inserted {
    color: ${colors.lime700};
  }

  .token.operator,
  .token.entity,
  .token.url,
  .language-css .token.string,
  .style .token.string,
  .token.variable {
    color: ${colors.pink100};
  }

  .token.atrule,
  .token.attr-value,
  .token.function,
  .token.class-name {
    color: ${colors.lime400};
  }

  .token.keyword {
    color: ${colors.skyblue400};
  }

  .token.regex,
  .token.important {
    color: ${colors.orange500};
  }

  /* @todo check available font weights/styles */
  .token.important,
  .token.bold {
    font-weight: bold;
  }
  .token.italic {
    font-style: italic;
  }
  
  .code-toolbar {
    position: relative;
    
    .copy-to-clipboard-button {
      position: absolute;
      top: ${pxToRem(4)};
      right: ${pxToRem(4)};
      border: none;
      padding: ${pxToRem(4)};
      border-radius: ${pxToRem(4)};
      transition: ${transition()};
      cursor: pointer;
      background: ${colors.neutral1000};
      color: ${colors.snow};
      opacity: 0;
      
      &:hover,
      &:focus {
        background: ${colors.violet600};
      }
    }
    
    &:hover,
    &:focus {
      .copy-to-clipboard-button {
        opacity: 1;
      }
    }
  }
  /* end code highlighting */

  img {
    object-fit: scale-down;
    max-width: 100%;
  }

  .callout-muted {
    padding: ${pxToRem(40)};
    margin-bottom: ${pxToRem(32)};
    border-radius: ${pxToRem(32)};
    background: ${colors.neutral900};
    
    > *:last-child {
      margin-bottom: 0;
    }
  }
  
  .callout-strong {
    padding: ${pxToRem(40)};
    margin-bottom: ${pxToRem(32)};
    border-radius: ${pxToRem(32)};
    background: #6046ff;
    color: ${colors.snow};
    
    > *:last-child {
      margin-bottom: 0;
    }
    
    ul li::before {
      background: ${colors.snow};
    }

    .btn {
      position: relative;
      display: inline-flex;
      justify-content: center;
      padding: ${pxToRem(10)} ${pxToRem(18)};
      border: ${pxToRem(2)} solid ${colors.white};
      border-radius: ${pxToRem(32)};
      font-family: "Space Grotesk", sans-serif;
      font-size: ${pxToRem(16)};
      line-height: 1;
      text-align: center;
      text-decoration: none;
      font-weight: ${weights.bold};
      transition: ${transition()};
      white-space: nowrap;
      background: transparent;
      color: ${colors.white};

      &:hover,
      &:active {
        background: ${colors.white};
        border-color: ${colors.white};
        color: ${colors.black900};
      }
      
      &::before {
        content: '';
        position: absolute;
        top: ${pxToRem(-4)};
        right: ${pxToRem(-4)};
        bottom: ${pxToRem(-4)};
        left: ${pxToRem(-4)};
        border-radius: inherit;
        box-shadow: none;
        transition: ${transition()};
      }
      
      ${mediaQueries.md} {
        padding: ${pxToRem(18)} ${pxToRem(22)};
      }
      
      ${mediaQueries.xl} {
        padding: ${pxToRem(17)} ${pxToRem(30)};
        border-radius: ${pxToRem(40)};
        font-size: ${pxToRem(18)};
      }
      
      &:focus-visible {
        outline: none;
        border-color: ${rgbaFromHex(colors.neutral400, 30)};
        
        &::before {
          box-shadow: 0 0 ${pxToRem(2)} ${pxToRem(2)} ${colors.yellow600};
        }
      }
    }
  }

  .callout-strong--two-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    ${mediaQueries.lg} {
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
      text-align: left;
    }

    p {
      font-family: "Space Grotesk", sans-serif;
      ${fontSizeLineHeight(18,28)}

      ${mediaQueries.lg} {
        margin-bottom: 0;
        margin-right: ${pxToRem(40)};
        ${fontSizeLineHeight(22,36)}
      }
    }
  }

  .callout-strong--icon {
    > :first-child {
      position: relative;
      padding-top: ${pxToRem(48)};

      ${mediaQueries.lg} {
        padding-top: 0;
        padding-left: ${pxToRem(40)};
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: ${pxToRem(40)};
        background-size: ${pxToRem(40)} auto;
        background-position: top center;
        background-repeat: no-repeat;

        ${mediaQueries.lg} {
          background-size: ${pxToRem(32)} auto;
          background-position: top left;
          height: 100%;
          width: ${pxToRem(32)};
        }
      }
    }
  }

  .callout-strong--icon-leaf {
    > :first-child::before {
      background-image: url('/greener-regions/leaf-icon.svg');
    }
  }
`;

export const blogBodyStyles = css`
  img {
    border-radius: ${pxToRem(16)};
  }

  pre, code {
    white-space: pre;
  }
  
  h2 {
    ${h3}
  }
  
  h3 {
    ${h4}
  }
  
  h4 {
    ${h5}
  }
  
  h5 {
    ${h6}
    
    ${mediaQueries.md} {
      ${fontSizeLineHeight(24, 36)}
    }

    ${mediaQueries.lg} {
      ${fontSizeLineHeight(24, 36)}
    }
  }
  
  video {
    max-width: 100%;
    margin-bottom: ${pxToRem(16)};
    border-radius: ${pxToRem(16)};
  }
`;
